import { Download, FileText, Image, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fileUrl } from "../lib/utils";


const MediaManager = () => {
  const logos = [
    { id: 1, title: "Dr. Schrammek Circle Logo - High Resolution", format: "PNG", size: "125 KB", category: "Logo", key: "logos/logo_tom.png" },
    { id: 2, title: "Dr. Schrammek Circle Logo - Medium Resolution", format: "PNG", size: "58 KB", category: "Logo", key: "logos/logo_dund.png" },
    { id: 3, title: "Dr. Schrammek Circle Logo - Low Resolution", format: "JPEG", size: "70 KB", category: "Logo", key: "logos/logo_jijig.jpg" },
    { id: 4, title: "Dr. Schrammek Circle Logo - White frame", format: "PNG", size: "180 KB", category: "Logo", key: "logos/logo_white_frame.png" },
    { id: 5, title: "Dr. Schrammek Landscape Logo - White background", format: "PNG", size: "48 KB", category: "Logo", key: "logos/logo_landscape.png" },
    { id: 6, title: "Dr. Schrammek Landscape Logo - Transparent background", format: "PNG", size: "59 KB", category: "Logo", key: "logos/logo_transparent.png" },
    { id: 7, title: "Dr. Schrammek Logo", format: " PNG", size: "133 KB", category: "Logo", key: "logos/logo_plain.png" },
  ];

  const fonts = [
    { id: 1, title: "CS Raleway Bold", format: "TTF", size: "92 KB", key: "fonts/cs-raleway-bold.ttf" },
    { id: 2, title: "CS Raleway Medium", format: "TTF", size: "91 KB", key: "fonts/cs-raleway-medium.ttf" },
    { id: 3, title: "CS Raleway Regular", format: "TTF", size: "47 KB", key: "fonts/cs-raleway-regular.ttf" },
    { id: 4, title: "CS Raleway SemiBold", format: "TTF", size: "93 KB", key: "fonts/cs-raleway-semibold.ttf" },
    { id: 5, title: "CS Raleway", format: "TTF", size: "92 KB", key: "fonts/cs-raleway.ttf" },
    { id: 6, title: "Domaine Text Regular", format: "TTF", size: "157 KB", key: "fonts/Domaine Text Regular.ttf" },
    { id: 7, title: "Domaine Display Regular", format: "OTF", size: "154 KB", key: "fonts/DOMAINEDISPLAY-REGULAR.OTF" },
    { id: 8, title: "Domaine Text Bold", format: "OTF", size: "227 KB", key: "fonts/DOMAINETEXT-BOLD.OTF" },
    { id: 9, title: "Domaine Text Regular", format: "OTF", size: "226 KB", key: "fonts/DOMAINETEXT-REGULAR.OTF" },
    { id: 10, title: "Raleway Italic", format: "TTF", size: "145 KB", key: "fonts/Raleway-Italic.ttf" },
  ];

const downloadFile = async (key: string, filename?: string) => {
  const apiUrl = import.meta.env.VITE_API_URL;
 
  const url = `${apiUrl}/read-file?key=${encodeURIComponent(key)}&inline=true`;


  window.open(url, '_blank');
};

  const MediaGrid = ({ 
    items, 
    icon: Icon, 
    title, 
    isLogo = false 
  }: { 
    items: any[]; 
    icon: any; 
    title: string;
    isLogo?: boolean;
  }) => (
    <>
      <h2 className="text-2xl font-semibold mb-6 col-span-full">{title}</h2>
      {items.map((item) => (
        <Card key={item.id} className="hover:shadow-lg transition-shadow flex flex-col h-full overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              {/* Thumbnail only for logos */}
              {isLogo && (
                <div className="flex-shrink-0 w-20 h-20 bg-muted/50 border-2 border-dashed rounded-lg overflow-hidden">
                  <img
                    src={fileUrl(item.key)}
                    alt={item.title}
                    className="w-full h-full object-contain p-2"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  {/* Fallback icon if image fails */}
                  <div className="hidden w-full h-full flex items-center justify-center">
                    <Image className="h-10 w-10 text-muted-foreground" />
                  </div>
                </div>
              )}

              <div className={isLogo ? "flex-1 min-w-0" : "w-full"}>
                <CardTitle className="text-lg mb-2 line-clamp-2 leading-tight">
                  {item.title}
                </CardTitle>
                <CardDescription>
                  <span className="inline-block bg-muted px-2 py-1 rounded text-xs font-medium">
                    {item.category || "Font"}
                  </span>
                </CardDescription>
              </div>

              {/* Icon for fonts or fallback */}
              {!isLogo && (
                <div className="text-primary flex-shrink-0">
                  <Icon className="h-10 w-10" />
                </div>
              )}
            </div>
          </CardHeader>

          <div className="mt-auto">
            <CardContent className="pt-0">
              <div className="flex items-center justify-between mb-3 text-sm text-muted-foreground">
                <span>{item.format}</span>
                <span>{item.size}</span>
              </div>
              <Button 
                className="w-full" 
                size="sm"
                onClick={() => downloadFile(item.key, item.title,)}
              >
                <Download className="h-4 w-4 mr-2" />
                Татах
              </Button>
            </CardContent>
          </div>
        </Card>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            {/* <h1 className="text-4xl font-bold mb-4">Медиа, хэрэглээний файл</h1> */}
            <h1 className="text-4xl font-bold mb-4">
              Хэрэгцээт файлууд
            </h1>
            <p className="text-muted-foreground text-lg">
              Лого, фонт эндээс татаж авна уу
            </p>
          </div>

          <Tabs defaultValue="logos" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="logos">Лого</TabsTrigger>
              <TabsTrigger value="fonts">Фонт</TabsTrigger>
            </TabsList>

            <TabsContent value="logos">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <MediaGrid items={logos} icon={Image} title="Бүх лого" isLogo={true} />
              </div>
            </TabsContent>

            <TabsContent value="fonts">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <MediaGrid items={fonts} icon={Type} title="Брэндийн фонтууд" isLogo={false} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MediaManager;